import * as ExcelJS from "exceljs";
//import jsPDF from "jspdf";
import { saveAs } from "file-saver";
//import html2canvas from "html2canvas";
import { loadDependencies } from "./ExcelCurved"; 

const titleStyle: Partial<ExcelJS.Style> = {
  font: { bold: true, size: 14 },
  alignment: { horizontal: "center", vertical: "middle" },
};

const headerStyle: Partial<ExcelJS.Style> = {
  font: { bold: true, color: { argb: "FFFFFF" }, size: 12 },
  fill: { type: "pattern", pattern: "solid", fgColor: { argb: "4F81BD" } },
  alignment: { horizontal: "center", vertical: "middle" },
  border: { top: { style: "thin" }, bottom: { style: "thin" } },
};

const evenRowStyle: Partial<ExcelJS.Style> = {
  fill: { type: "pattern", pattern: "solid", fgColor: { argb: "D9E1F2" } },
};

const oddRowStyle: Partial<ExcelJS.Style> = {
  fill: { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFFF" } },
};

export const exportToExcel = async (
  chartRef: React.RefObject<HTMLDivElement>,
  chartData: Array<any>,
  revenueData: any[]
) => {
  if (!chartRef.current || revenueData.length === 0) return;

  await loadDependencies(); // Load dependencies before execution

  // const canvas = await html2canvas(chartRef.current, {
  //   scale: 2,
  //   useCORS: true,
  //   backgroundColor: "#fff",
  // } as any);

  // const chartImage = canvas.toDataURL("image/png");

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Report");

  try {
    const response = await fetch("/Lg.png");
    const imageBlob = await response.blob();
    const arrayBuffer = await imageBlob.arrayBuffer();
    const uint8Array = new Uint8Array(arrayBuffer);
    const imageId = workbook.addImage({
      buffer: uint8Array,
      extension: "png",
    });

    const headers = Object.keys(chartData[0] || {});
    const totalColumns = headers.length;
    const assetName = revenueData[0]?.ASSET_NAME || "Unknown Asset";
    const assetType = revenueData[0]?.ASSET_TYPE || "Unknown Type";

    worksheet.addImage(imageId, {
      tl: { col: Math.floor(totalColumns / 2) - 1, row: 0 },
      ext: { width: 200, height: 60 },
    });

    worksheet.mergeCells("A3:B3");
    worksheet.getCell("A3").value = `Asset Name: ${assetName}`;
    worksheet.getCell("A3").style = titleStyle;
    worksheet.getCell("A3").alignment = { horizontal: "left", vertical: "middle" };

    worksheet.mergeCells("A4:B4");
    worksheet.getCell("A4").value = `Asset Type: ${assetType}`;
    worksheet.getCell("A4").style = titleStyle;
    worksheet.getCell("A4").alignment = { horizontal: "left", vertical: "middle" };

    worksheet.getCell(`A1`).value = "ESTIMATED SETTLEMENTS";
    worksheet.getCell(`A1`).style = titleStyle;

    worksheet.addRow([]);

    const headerRow = worksheet.addRow(headers);
    headerRow.eachCell((cell) => {
      cell.style = headerStyle;
    });

    chartData.forEach((row, index) => {
      const dataRow = worksheet.addRow(Object.values(row));
      dataRow.eachCell((cell) => {
        cell.style = index % 2 === 0 ? evenRowStyle : oddRowStyle;
      });
    });

    worksheet.columns.forEach((col, i) => {
      if (col) col.width = headers[i]?.length + 7;
    });

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer], { type: "application/octet-stream" }), "Chart_Report.xlsx");
  } catch (error) {
    console.error("Error loading image:", error);
  }
};

export const exportToPDF = async (
  chartRef: React.RefObject<HTMLDivElement>,
  chartData: Array<any>,
  revenueData: any[],
  fileName: string
) => {
  if (!chartRef.current || revenueData.length === 0) {
    console.error("Chart reference is null or revenue data is empty.");
    return;
  }

  await loadDependencies(); // Ensure dependencies are loaded
   // Assign jsPDF explicitly after ensuring it's loaded
  // Explicitly import jsPDF and html2canvas
  const { default: jsPDF } = await import("jspdf");
  const { default: html2canvas } = await import("html2canvas");
  const doc = new jsPDF({ orientation: "landscape" });

  try {
    const logo = await fetch("/Lg.png");
    const logoBlob = await logo.blob();
    const logoURL = URL.createObjectURL(logoBlob);
    const img = new Image();
    img.src = logoURL;

    await new Promise((resolve) => {
      img.onload = () => resolve(true);
    });

    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.text("ESTIMATED SETTLEMENTS", 12, 25);

    const assetName = revenueData[0]?.ASSET_NAME || "Unknown Asset";
    const assetType = revenueData[0]?.ASSET_TYPE || "Unknown Type";
    doc.setFontSize(12);
    doc.text(`Asset Name: ${assetName}`, 14, 40);
    doc.text(`Asset Type: ${assetType}`, 14, 48);

    doc.addImage(img, "PNG", 120, 10, 60, 20);

    const canvas = await html2canvas(chartRef.current, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#fff",
    }as any);

    const imgData = canvas.toDataURL("image/png");
    doc.addImage(imgData, "PNG", 10, 70, 270, 100);

    doc.save(`${fileName}.pdf`);
  } catch (error) {
    console.error("Error capturing chart:", error);
  }
};


export const exportToCSV = (chartData: Array<any>, fileName: string) => {
  if (chartData.length === 0) {
    console.error("Chart data is empty.");
    return;
  }

  // const headers = Object.keys(chartData[0]);
  const headers = Object.keys(chartData[0] || {});
  const csvRows = [headers.join(",")];

  chartData.forEach((row) => {
    const values = headers.map((header) => `"${row[header] || ""}"`);
    csvRows.push(values.join(","));
  });

  const csvContent = csvRows.join("\n");
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });

  saveAs(blob, `${fileName}.csv`);
};
